import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef, ElementRef, Renderer2, HostBinding, inject, viewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ISpeechToTextTranscriptChunk } from "../../../../core-ui/models/interfaces";
import { TranscriptService } from "../../../../core-ui/services/transcript.service";
import { filter } from "rxjs/operators";
import { ThemeService } from "../../../../core-ui/services/theme.service";
import { SizeEnum } from "../../../../core-ui/core-ui.enums";
import { DeviceService } from "../../../../core-ui/services/device.service";
import { Util } from "@auvious/common";

@Component({
  selector: "app-captions",
  templateUrl: "./captions.component.html",
  styleUrls: ["./captions.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CaptionsComponent implements OnInit {
  private cd = inject(ChangeDetectorRef);
  private asr = inject(TranscriptService);
  private host = inject<ElementRef<HTMLDivElement>>(ElementRef);
  private theme = inject(ThemeService);
  private renderer = inject(Renderer2);
  private destroyRef = inject(DestroyRef);
  batchChunkMap: Map<string, ISpeechToTextTranscriptChunk> = new Map();
  userFinalChunkMap: Map<string, boolean> = new Map();
  userCurrentBatchMap: Map<string, string> = new Map();

  supportsBackdropBlur = false;
  ttl = 5 * 1000;

  readonly captionsRef = viewChild<ElementRef<HTMLDivElement>>("captions");

  @HostBinding("class") get class() {
    return {
      blur: this.supportsBackdropBlur,
      "is-mobile": DeviceService.isMobile,
    };
  }

  ngOnInit(): void {
    this.supportsBackdropBlur = !DeviceService.isFirefox;

    this.theme.themeChanged$().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((theme) => {
      if (!theme) {
        return;
      }
      let fontSize;
      switch (theme.captionsFontSize) {
        case SizeEnum.small:
          fontSize = "1.1em";
          break;
        case SizeEnum.medium:
          fontSize = "1.2em";
          break;
        case SizeEnum.large:
          fontSize = "1.3em";
          break;
      }
      this.renderer.setStyle(
        this.host.nativeElement,
        "color",
        theme.captionsFontColor
      );
      this.renderer.setStyle(
        this.host.nativeElement,
        "background-color",
        theme.captionsBackgroundColor
      );
      this.renderer.setStyle(this.host.nativeElement, "font-size", fontSize);
    });

    this.asr.chunk$.pipe(
      filter((c) => !!c),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((chunk) => {
      const wasFinal = this.userFinalChunkMap.get(chunk.userId);

      if (wasFinal || !this.userCurrentBatchMap.has(chunk.userId)) {
        const batchId = Util.uuidgen();
        this.userCurrentBatchMap.set(chunk.userId, batchId);
      }
      this.userFinalChunkMap.set(chunk.userId, chunk.isFinal);

      const currentBatch = this.userCurrentBatchMap.get(chunk.userId);

      this.batchChunkMap.set(currentBatch, chunk);

      if (chunk.isFinal) {
        setTimeout(() => {
          this.batchChunkMap.delete(currentBatch);
          this.cd.detectChanges();
        }, this.ttl);
      }

      this.cd.detectChanges();
      this.scrollToBottom();
    });

    this.asr.reset$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((_) => {
      this.batchChunkMap = new Map();
      this.userFinalChunkMap = new Map();
      this.userCurrentBatchMap = new Map();
      this.cd.detectChanges();
    });
  }

  scrollToBottom() {
    this.host.nativeElement.scrollTo({
      behavior: "smooth",
      top: this.captionsRef().nativeElement.clientHeight,
    });
  }

  trackChunk(index, el: ISpeechToTextTranscriptChunk) {
    return el.userId;
  }

  get chunks(): ISpeechToTextTranscriptChunk[] {
    // get the last 2 items
    return Array.from(this.batchChunkMap.values()).slice(-2);
  }
}
